import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';
import { TableSkeleton } from '../components/shared/LoaderSkeleton';

// Premium Insurance Companies Index - Architectural Harmony with InvoiceShow
export default function InsuranceCompaniesIndex() {
    const location = useLocation();
    const [companies, setCompanies] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });

    useEffect(() => {
        loadCompanies();
    }, [location, currentPage, searchTerm]);

    const loadCompanies = async () => {
        try {
            setLoading(true);
            const params: any = { 
                page: currentPage,
                q: searchTerm
            };
            const response = await api.get('/insurance_companies', { params });
            if (response.success) {
                const companiesData = response.data.data || response.data || [];
                setCompanies(companiesData);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (error) {
            console.error('Error loading insurance companies:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this insurance company?')) return;

        try {
            await api.delete(`/insurance_companies/${id}`);
            loadCompanies();
        } catch (error) {
            alert('Error deleting company. It may be in use.');
        }
    };

    if (loading && companies.length === 0) return <TableSkeleton />;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/30 min-h-screen">
            {/* Header / Actions - Styled like InvoiceShow */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Link to="/" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                            Insurance <span className="text-indigo-600">Providers</span>
                        </h1>
                        <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {pagination.total_count} Profiles
                        </span>
                    </div>
                    <p className="text-sm text-slate-500 ml-10">
                        Managing coverage and <span className="font-semibold text-slate-700">underwriting entities</span>
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <Link
                        to="/insurance_companies/new"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-black transition-all shadow-sm shadow-slate-200"
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
                        Add Provider
                    </Link>
                </div>
            </div>

            {/* Metrics Row - Segmented Layout */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-slate-100 uppercase tracking-widest mb-8">
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Registry Density</h3>
                    <p className="text-xl font-black text-slate-900 font-mono">{pagination.total_count}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Identified Entities</p>
                </div>
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Active Portfolio</h3>
                    <p className="text-xl font-black text-indigo-600 font-mono">{companies.filter(c => c.is_active).length}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Operational status</p>
                </div>
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Liaison Count</h3>
                    <p className="text-xl font-black text-emerald-600 font-mono">{companies.filter(c => c.contact_person).length}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Assigned Officers</p>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
                <div className="relative group max-w-md">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search providers..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-11 pr-4 py-3 bg-white rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm font-medium transition-all shadow-sm"
                    />
                </div>
            </div>

            {/* Data Table - Refined Style */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-8 transition-all hover:shadow-md">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100 uppercase tracking-widest text-[10px] font-black text-slate-400">
                                <th className="px-6 py-4">Establishment</th>
                                <th className="px-6 py-4">Liaison Officer</th>
                                <th className="px-6 py-4">Communication Details</th>
                                <th className="px-6 py-4 text-center">Status</th>
                                <th className="px-6 py-4 text-right">Operations</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {companies.map((company: any) => (
                                <tr key={company.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <Link to={`/insurance_companies/${company.id}`} className="font-bold text-slate-900 hover:text-indigo-600 transition-colors">
                                            {company.company_name}
                                        </Link>
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">ID: INS-{company.id.toString().padStart(3, '0')}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-semibold text-slate-700">{company.contact_person || 'No Lead Assigned'}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1.5 text-slate-600 font-medium text-[11px]">
                                                <svg className="w-3 h-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                                                {company.phone || 'N/A'}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-slate-400 text-[11px]">
                                                <svg className="w-3 h-3 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                                                {company.email || 'N/A'}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-tighter ${
                                            company.is_active ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'
                                        }`}>
                                            {company.is_active ? 'Operational' : 'Suspended'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-1 underline-none">
                                            <Link
                                                to={`/insurance_companies/${company.id}/edit`}
                                                className="p-2 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(company.id)}
                                                className="p-2 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {companies.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400 italic text-sm">
                                        No insurance providers identified in the registry.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Alignment */}
            <div className="flex justify-end pt-2">
                <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.total_count}
                    perPage={pagination.per_page}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}
